import { useState, useEffect } from 'react';
import authService from '@/services/auth';

interface Tenant {
  id: string;
  name: string;
  code: string;
}

interface PontBascule {
  id: string;
  code: string;
  name: string;
  location: string;
  description: string;
  is_default: boolean;
  status: string;
  ws_url: string;
  port: number; // Port HTTP
  port_ws: number; // Port WebSocket
  scale_unit: string;
  scale_precision: number;
  stability_threshold: number;
  network_ip_address: string;
  agent_local_ip: string;
  agent_mac_address: string;
  agent_hostname: string;
  agent_platform: string;
  agent_python_version: string;
  agent_version: string;
  agent_installation_status: string;
  agent_network_status: string;
  agent_service_status: string;
  agent_service_name: string;
  agent_health_endpoint: string;
  agent_weigh_read_endpoint: string;
  agent_weigh_stream_endpoint: string;
  agent_balance_connected: boolean;
  agent_balance_model: string;
  agent_install_date: string;
  agent_last_communication: string;
  apiKey: string;
  require_authentication: boolean;
  last_calibration: string;
  next_calibration: string;
  created_at: string;
  updated_at: string;
}

interface Site {
  id: string;
  name: string;
  code: string;
  location: string;
  description: string;
  tenant: Tenant;
  role: {
    id: number;
    name: string;
    description?: string;
  };
  is_default: boolean;
  assigned_at: string;
  ponts_bascule: PontBascule[];
}

interface Role {
  id: number;
  name: string;
  description: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  two_factor_status: {
    is_enabled: boolean;
    has_backup_codes: boolean;
    backup_codes_count: number;
  };
  role: Role | null;
  default_site: {
    id: string;
    name: string;
    code: string;
  } | null;
  sites: Site[];
  sites_count: number;
  has_sites: boolean;
  isAuthenticated: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = async () => {
    try {
      // Vérifier si l'utilisateur est authentifié
      if (!authService.isAuthenticated()) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Récupérer le profil utilisateur via le service auth
      const userProfile = await authService.getUserProfile();
      
      // Transformer le profil en format User
      setUser({
        ...userProfile,
        full_name: userProfile.full_name || '',
        phone: '',
        two_factor_status: {
          is_enabled: false,
          has_backup_codes: false,
          backup_codes_count: 0
        },
        role: userProfile.role ? {
          id: 0,
          name: userProfile.role.name,
          description: ''
        } : null,
        default_site: userProfile.default_site ? {
          id: userProfile.default_site,
          name: '',
          code: ''
        } : null,
        sites: [],
        sites_count: 0,
        has_sites: false,
        isAuthenticated: true
      });
      
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
      // En cas d'erreur, nettoyer les tokens et déconnecter
      authService.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  // Fonction pour mettre à jour les informations utilisateur
  const updateUser = (userData: User) => {
    setUser(userData);
  };

  // Fonction pour déconnecter l'utilisateur
  const logout = async () => {
    try {
      // Nettoyer l'utilisateur immédiatement
      setUser(null);
      
      // Appeler le service de déconnexion
      await authService.logout();
      
      // S'assurer que l'utilisateur est bien null
      setUser(null);
      
      // Rediriger vers la page de login après déconnexion
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      } else {
        // Si déjà sur la page de login, forcer un rechargement
        window.location.reload();
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Même en cas d'erreur, nettoyer l'utilisateur et rediriger
      setUser(null);
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  };

  // Fonction pour rafraîchir les informations utilisateur
  const refreshUser = async () => {
    setLoading(true);
    await loadUser();
  };

  // Fonction utilitaire pour obtenir le site par défaut
  const getDefaultSite = (): Site | null => {
    if (!user) return null;
    return user.sites.find(site => site.is_default) || user.sites[0] || null;
  };

  // Fonction utilitaire pour obtenir tous les ponts bascule de l'utilisateur
  const getAllPontsBascule = (): PontBascule[] => {
    if (!user) return [];
    return user.sites.flatMap(site => site.ponts_bascule);
  };

  // Fonction utilitaire pour obtenir les ponts bascule d'un site spécifique
  const getPontsBasculeBySite = (siteId: string): PontBascule[] => {
    if (!user) return [];
    const site = user.sites.find(s => s.id === siteId);
    return site ? site.ponts_bascule : [];
  };

  return { 
    user, 
    loading, 
    isAuthenticated: !!user,
    updateUser, 
    logout,
    refreshUser,
    getDefaultSite,
    getAllPontsBascule,
    getPontsBasculeBySite
  };
};
